import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "jobsite:inline-flex jobsite:items-center jobsite:justify-center jobsite:gap-2 jobsite:whitespace-nowrap jobsite:text-sm jobsite:font-medium jobsite:transition-all jobsite:active:scale-[0.98] jobsite:disabled:pointer-events-none jobsite:disabled:opacity-50 jobsite:[&_svg]:pointer-events-none jobsite:[&_svg:not([class*=size-])]:size-4 jobsite:shrink-0 jobsite:[&_svg]:shrink-0 jobsite:outline-none jobsite:focus-visible:border-ring jobsite:focus-visible:ring-ring/50 jobsite:focus-visible:ring-[3px] jobsite:aria-invalid:ring-destructive/20 jobsite:dark:aria-invalid:ring-destructive/40 jobsite:aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "jobsite:bg-primary jobsite:text-primary-foreground jobsite:shadow-sm jobsite:hover:bg-primary/90 jobsite:hover:shadow-md",
        destructive:
          "jobsite:bg-destructive jobsite:text-white jobsite:hover:bg-destructive/90 jobsite:focus-visible:ring-destructive/20 jobsite:dark:focus-visible:ring-destructive/40 jobsite:dark:bg-destructive/60",
        outline:
          "jobsite:border-2 jobsite:border-border jobsite:bg-background jobsite:shadow-xs jobsite:hover:bg-muted jobsite:dark:bg-input/30 jobsite:dark:border-input jobsite:dark:hover:bg-input/50",
        secondary:
          "jobsite:bg-secondary jobsite:text-secondary-foreground jobsite:hover:bg-secondary/80",
        ghost:
          "jobsite:hover:bg-accent jobsite:hover:text-accent-foreground jobsite:dark:hover:bg-accent/50",
        link: "jobsite:text-primary jobsite:underline-offset-4 jobsite:hover:underline",
      },
      radius: {
        default: "",
        rounded: "jobsite:rounded-md",
      },
      size: {
        default: "jobsite:h-9 jobsite:px-4 jobsite:py-2 jobsite:has-[>svg]:px-3",
        sm: "jobsite:h-8 jobsite:gap-1.5 jobsite:px-3 jobsite:has-[>svg]:px-2.5",
        lg: "jobsite:h-10 jobsite:px-6 jobsite:has-[>svg]:px-4",
        icon: "jobsite:size-9",
        "icon-sm": "jobsite:size-8",
        "icon-lg": "jobsite:size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      radius: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  radius,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, radius, className }))}
      style={{ borderRadius: "var(--jobsite-radius)" }}
      {...props}
    />
  );
}

export { Button, buttonVariants };
